import { CloseOutlined } from '@ant-design/icons';
import { Checkbox, Heading, SearchInput } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import ArrowIcon, { ARROW_TYPES } from '@mm/icons/general/ArrowIcon';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useInfiniteScroll } from '../../../../../common/hooks/use-infinite-scroll';
import styles from './AudienceListSidebar.module.css';
import { useFetchAudiences } from './use-fetch-audiences';

interface Props {
  className: string;
  selectedAudienceId: number | undefined;
  navigateToAudience: (audienceId: number | undefined) => void;
  usedInAutomation?: boolean;
  onAudienceSelect?: (audience: AudiencesModel[]) => void;
  initialAudiences?: AudiencesModelWithDestroy[];
  withoutSystemFilters?: boolean;
}
export type AudiencesModelWithDestroy = AudiencesModel & { destroy?: boolean };

const VISISBLE_AUDIENCES = 5;

export const AudienceListSidebar = (props: Props) => {
  const {
    className,
    selectedAudienceId,
    navigateToAudience,
    usedInAutomation,
    onAudienceSelect,
    initialAudiences,
    withoutSystemFilters,
  } = props;

  const audiences = useSelector(selectAudienceSorted).filter(a =>
    withoutSystemFilters ? !a.hidden : true,
  );

  const contacts = useSelector(selectContacts);
  const [filter, setFilter] = useState<string>();
  const [selectedAudiences, setSelectedAudiences] = useState<
    AudiencesModelWithDestroy[]
  >(initialAudiences || []);
  const [showAllSelectedAudiences, setShowAllSelectedAudiences] =
    useState(false);
  const filteredAudiences = selectedAudiences.filter(aud => !aud.destroy);

  const debouncedFilter = useDebounce(filter, 250);
  const { fetch, fetchMore, pagination, isFetching } = useFetchAudiences({
    skipBusy: true,
    withoutSystemFilters,
    fitchFirstPageOnInit: true,
  });
  const { scrollContainerRef } = useInfiniteScroll<HTMLDivElement>({
    fetchMore,
    fetchDisabled: pagination.isLastPage,
  });

  useEffect(() => {
    if (debouncedFilter !== undefined) {
      fetch(1, 25, debouncedFilter);
    }
  }, [debouncedFilter, fetch]);

  const isSelectedAudience = (audience: AudiencesModel) => {
    return !!selectedAudiences
      .filter(aud => !aud.destroy)
      .find(selectedAudience => audience.id === selectedAudience.id);
  };

  const onCheckboxClick = (audience: AudiencesModel) => {
    const updatedSelectedAudiences = selectedAudiences.filter(
      a => a.id !== audience.id,
    );
    if (isSelectedAudience(audience)) {
      const audiencesToDestroy = selectedAudiences
        .filter(a => a.id === audience.id)
        .map(a => ({
          ...a,
          destroy: true,
        }));

      setSelectedAudiences([
        ...updatedSelectedAudiences,
        ...audiencesToDestroy,
      ]);
      onAudienceSelect?.([...updatedSelectedAudiences, ...audiencesToDestroy]);
    } else {
      setSelectedAudiences([...updatedSelectedAudiences, audience]);
      onAudienceSelect?.([...updatedSelectedAudiences, audience]);
    }
  };

  return (
    <div className={classNames(className, styles.container)}>
      <div className={styles.heading}>
        <Heading size={usedInAutomation ? 4 : 5}>
          {usedInAutomation ? 'Choose Audience' : 'Audiences'}
        </Heading>
        {!usedInAutomation && (
          <div className={styles.headingInfo}>
            To create a new audience, apply filters, then click 'Save as an
            Audience'
          </div>
        )}
        <SearchInput
          css={{
            container: usedInAutomation
              ? styles.searchInputAutomation
              : styles.searchInput,
            icon: styles.searchIcon,
          }}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search Audience..."
        />
      </div>
      {usedInAutomation && selectedAudiences.length ? (
        <div className={styles.chosenAudiencesContainer}>
          <div className={styles.chosenAudiencesHeader}>
            <div>Chosen Audiences</div>
            <div
              className={styles.reset}
              onClick={() => {
                setSelectedAudiences([]);
                onAudienceSelect?.(
                  selectedAudiences.map(a => ({
                    ...a,
                    destroy: true,
                  })),
                );
              }}
            >
              Reset all
            </div>
          </div>
          {filteredAudiences.map((audience, idx) => {
            return (
              <div
                key={audience.id}
                className={classNames(styles.chosenAudienceItem, {
                  [styles.hiddenAudienceItem]:
                    idx >= VISISBLE_AUDIENCES && !showAllSelectedAudiences,
                })}
              >
                <div className={styles.chosenAudience}>
                  <div className={styles.audienceName}>{audience.name}</div>
                  <div className={styles.audienceContactCount}>
                    {audience.contactsCount}
                  </div>
                </div>
                <CloseOutlined
                  className={styles.close}
                  onClick={() => onCheckboxClick(audience)}
                />
              </div>
            );
          })}
          {filteredAudiences.length > VISISBLE_AUDIENCES && (
            <div
              className={styles.collapseExpandContainer}
              onClick={() =>
                setShowAllSelectedAudiences(!showAllSelectedAudiences)
              }
            >
              {showAllSelectedAudiences
                ? 'Collapse'
                : `View full list (${
                    filteredAudiences.length - VISISBLE_AUDIENCES
                  })`}
              <ArrowIcon
                direction={showAllSelectedAudiences ? 'top' : 'bottom'}
                color="#8c8c8c"
                size={12}
                arrowType={ARROW_TYPES.CHEVRON}
              />
            </div>
          )}
        </div>
      ) : null}

      <div
        className={
          usedInAutomation ? styles.audiencesFlex : styles.audiencesGrid
        }
        ref={scrollContainerRef}
      >
        <div
          className={classNames(styles.audience, styles.allContacts, {
            [styles.audience__selected]: selectedAudienceId === undefined,
            [styles.audience__flex]: usedInAutomation,
          })}
          onClick={() => navigateToAudience(undefined)}
        >
          <div className={styles.audienceName}>
            <strong>All Contacts</strong>
          </div>
          <div className={styles.audienceContactCountContainer}>
            <div className={styles.audienceContactCount}>
              {contacts.pagination.total}
            </div>
          </div>
        </div>

        {audiences?.map(audience =>
          usedInAutomation ? (
            <div
              className={classNames(styles.audience__flex, {
                [styles.audience__selected]: selectedAudienceId === audience.id,
              })}
              key={audience.id}
            >
              <Checkbox
                className={styles.checkbox}
                isChecked={isSelectedAudience(audience)}
                onClick={() => onCheckboxClick(audience)}
              />
              <div
                className={styles.audienceWrapper}
                onClick={() => navigateToAudience(audience.id)}
              >
                <div className={styles.audienceName}>{audience.name}</div>
                <div className={styles.audienceContactCount}>
                  {audience.contactsCount}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={classNames(styles.audience, {
                [styles.audience__selected]: selectedAudienceId === audience.id,
              })}
              key={audience.id}
              onClick={() => navigateToAudience(audience.id)}
            >
              <div className={styles.audienceName}>{audience.name}</div>
              <div className={styles.audienceContactCountContainer}>
                <div className={styles.audienceContactCount}>
                  {audience.contactsCount}
                </div>
              </div>
            </div>
          ),
        )}
      </div>
      {isFetching && (
        <div className={styles.loading}>
          <i className="fas fa-spinner fa-spin" />
          <span>Loading audiences...</span>
        </div>
      )}
    </div>
  );
};
